/**
 * DetectiveFooter — floating walking detective + speech bubble.
 *
 * Two states, driven by `sherlock:buddy-context` job events:
 *
 *   ── ACTIVE (running investigation/analysis) ──
 *   The line-drawn stick-figure detective walks back-and-forth across a
 *   25%-wide track anchored to the right edge. A speech bubble rides
 *   above him, anchored to his walking position.
 *
 *   ── IDLE (no running job) ──
 *   The detective is stationary, parked in the bottom-right corner at
 *   10% inset from both edges. The bubble sits above him in place.
 *
 * In both states the container has no background — the buddy "floats" over
 * page content. Page content remains fully interactive (pointerEvents:none
 * on the container; only the buddy's own footprint re-enables them).
 *
 * Reduced-motion: static "🕵🏽 ▸ {label}" text at the corner, no movement.
 *
 * #3: Smoke cadence — 45s cycle (was 90s). Burst fires at 10s (was 4s).
 *     Burst puff count bumped to 16.
 * #4: Double-click triggers MegaSmokeCurtain (40 puffs, bottom half-screen).
 * #5: Sprite redesigned with deerstalker hat, Inverness cape, pipe smoke,
 *     subtle breathing animation, occasional wink every ~12s.
 */

import { Box } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { BuddyBubble } from '@/components/BuddyBubble';
import { analyses, intake, investigate } from '@/helpers/ApiHelper';
import { useSidebarState } from '@/hooks/useSidebarState';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { isAnalysisFailed, isAnalysisResult } from '@/types/analysis';

export interface DetectiveFooterProps {
  /**
   * aria-label for the status region — used for a11y only, not displayed.
   * Defaults to "🕵🏽 investigating…".
   */
  label?: string;
  /**
   * When set, pins the BuddyBubble to this text instead of cycling.
   * Back-compat shim for call sites that previously passed `label` for
   * the visible status text.
   */
  staticLabel?: string;
  /** Optional action node rendered on the right (e.g. <Button>stop</Button> or a live chip) */
  action?: ReactNode;
}

const SIDEBAR_EXPANDED = 240;
const SIDEBAR_COLLAPSED = 72;

// ─── Pixel-art detective sprite (v3 — sharper 32×32 Sherlock) ────────────────
//
// 32-column × 32-row grid, rendered at 2 CSS px per sprite pixel → 64 × 64.
//
// Six logical states composed from props:
//   • idle                — front view, magnifier at hip
//   • idle + smoking      — pipe + smoke wisps visible
//   • idle + examining    — looking through magnifier (lens over eye)
//   • walking             — side profile, magnifier forward at hip
//   • walking + smoking   — profile + pipe + smoke trail
//   • walking + examining — profile looking through lens
//
// Color layers (back → front): outline → shadow → mid → highlight → cream
//   • OUTLINE_FILL    = #6e3320 (deeper than primary.dark; silhouette edge)
//   • SHADOW_FILL     = primary.dark      (#a85a3f)
//   • currentColor    = primary.main      (#D97757) body mid-tone
//   • HIGHLIGHT_AMBER = primary.light     (#f0a37c)
//   • HIGHLIGHT_FILL  = warm cream rgba — eyes, mustache, lens glint, smoke

const PIXEL = 2;
const SPRITE_COLS = 32;
const SPRITE_ROWS = 32;
const CHAR_WIDTH = SPRITE_COLS * PIXEL;
const CHAR_HEIGHT = SPRITE_ROWS * PIXEL;

const HIGHLIGHT_FILL = 'rgba(255, 245, 230, 0.95)';
const OUTLINE_FILL = '#6e3320';
const SHADOW_FILL = '#a85a3f';
const HIGHLIGHT_AMBER = '#f0a37c';

const BURST_PUFF_SIZE = PIXEL * 4;
const PUFF_SIZE = PIXEL * 3;
const IDLE_SMOKE_CYCLE_SECONDS = 45;

type SpritePixel = readonly [number, number];

// ─── Imperative sprite builder ───────────────────────────────────────────────
//
// Runs once at module load to produce typed pixel arrays per variant.
// Mirrors the JS preview at /tmp/buddy-preview.html that was signed off on.

interface SpriteLayers {
  outline: SpritePixel[];
  shadow: SpritePixel[];
  mid: SpritePixel[];
  highlight: SpritePixel[];
  cream: SpritePixel[];
}

type GridCell = 'O' | 'D' | 'M' | 'H' | 'C' | ' ';

function makeGrid(): GridCell[][] {
  return Array.from({ length: SPRITE_ROWS }, () =>
    Array.from({ length: SPRITE_COLS }, () => ' ' as GridCell),
  );
}
function setPx(g: GridCell[][], x: number, y: number, c: GridCell): void {
  if (y < 0 || y >= SPRITE_ROWS || x < 0 || x >= SPRITE_COLS) return;
  g[y]![x] = c;
}
function fillRect(g: GridCell[][], x1: number, y1: number, x2: number, y2: number, c: GridCell): void {
  for (let y = y1; y <= y2; y++) for (let x = x1; x <= x2; x++) setPx(g, x, y, c);
}
function gridToLayers(g: GridCell[][]): SpriteLayers {
  const out: SpriteLayers = { outline: [], shadow: [], mid: [], highlight: [], cream: [] };
  for (let y = 0; y < SPRITE_ROWS; y++) {
    for (let x = 0; x < SPRITE_COLS; x++) {
      const c = g[y]![x];
      if (c === 'O') out.outline.push([x, y]);
      else if (c === 'D') out.shadow.push([x, y]);
      else if (c === 'M') out.mid.push([x, y]);
      else if (c === 'H') out.highlight.push([x, y]);
      else if (c === 'C') out.cream.push([x, y]);
    }
  }
  return out;
}

function drawIdleBody(g: GridCell[][]): void {
  // Hat crown
  [[1, 12, 19], [2, 11, 20], [3, 10, 21], [4, 9, 22], [5, 9, 22], [6, 9, 22]]
    .forEach(([y, x1, x2]) => fillRect(g, x1!, y!, x2!, y!, 'M'));
  fillRect(g, 12, 0, 19, 0, 'O');
  setPx(g, 11, 1, 'O'); setPx(g, 20, 1, 'O');
  setPx(g, 10, 2, 'O'); setPx(g, 21, 2, 'O');
  setPx(g, 9, 3, 'O');  setPx(g, 22, 3, 'O');
  setPx(g, 8, 4, 'O');  setPx(g, 23, 4, 'O');
  setPx(g, 8, 5, 'O');  setPx(g, 23, 5, 'O');
  setPx(g, 8, 6, 'O');  setPx(g, 23, 6, 'O');
  fillRect(g, 12, 1, 19, 1, 'H');
  fillRect(g, 11, 2, 14, 2, 'H');
  fillRect(g, 10, 6, 21, 6, 'D');
  // Earflaps
  fillRect(g, 6, 7, 7, 8, 'M');
  setPx(g, 6, 7, 'H'); setPx(g, 7, 7, 'H');
  setPx(g, 6, 8, 'D'); setPx(g, 7, 8, 'D');
  fillRect(g, 24, 7, 25, 8, 'M');
  setPx(g, 24, 7, 'H'); setPx(g, 25, 7, 'H');
  setPx(g, 24, 8, 'D'); setPx(g, 25, 8, 'D');
  setPx(g, 5, 7, 'O'); setPx(g, 5, 8, 'O');
  setPx(g, 6, 9, 'O'); setPx(g, 7, 9, 'O');
  setPx(g, 26, 7, 'O'); setPx(g, 26, 8, 'O');
  setPx(g, 24, 9, 'O'); setPx(g, 25, 9, 'O');
  // Brim
  fillRect(g, 4, 8, 27, 8, 'H');
  fillRect(g, 5, 9, 26, 9, 'D');
  setPx(g, 3, 8, 'O'); setPx(g, 28, 8, 'O');
  setPx(g, 4, 9, 'O'); setPx(g, 27, 9, 'O');
  fillRect(g, 4, 10, 27, 10, 'O');
  // Face
  fillRect(g, 11, 11, 20, 11, 'M');
  fillRect(g, 10, 12, 21, 12, 'M');
  fillRect(g, 10, 13, 21, 13, 'M');
  fillRect(g, 10, 14, 21, 14, 'M');
  fillRect(g, 11, 15, 20, 15, 'M');
  fillRect(g, 12, 16, 19, 16, 'M');
  setPx(g, 10, 11, 'O'); setPx(g, 21, 11, 'O');
  setPx(g, 9, 12, 'O');  setPx(g, 22, 12, 'O');
  setPx(g, 9, 13, 'O');  setPx(g, 22, 13, 'O');
  setPx(g, 9, 14, 'O');  setPx(g, 22, 14, 'O');
  setPx(g, 10, 15, 'O'); setPx(g, 21, 15, 'O');
  setPx(g, 11, 16, 'O'); setPx(g, 20, 16, 'O');
  fillRect(g, 12, 17, 19, 17, 'O');
  fillRect(g, 11, 11, 20, 11, 'D');
  // Eyes (sclera + pupil)
  setPx(g, 12, 13, 'C'); setPx(g, 13, 13, 'O');
  setPx(g, 18, 13, 'O'); setPx(g, 19, 13, 'C');
  // Mustache
  fillRect(g, 14, 15, 17, 15, 'C');
  // Neck
  fillRect(g, 14, 18, 17, 18, 'D');
  setPx(g, 13, 18, 'O'); setPx(g, 18, 18, 'O');
  // Cape — narrow shoulder span (no T-pose silhouette)
  fillRect(g, 11, 19, 20, 19, 'M');
  fillRect(g, 10, 20, 21, 20, 'M');
  fillRect(g, 10, 21, 21, 21, 'M');
  fillRect(g, 11, 22, 20, 22, 'M');
  fillRect(g, 11, 19, 20, 19, 'H');
  setPx(g, 10, 19, 'O'); setPx(g, 21, 19, 'O');
  setPx(g, 9, 20, 'O');  setPx(g, 22, 20, 'O');
  setPx(g, 9, 21, 'O');  setPx(g, 22, 21, 'O');
  setPx(g, 10, 22, 'O'); setPx(g, 21, 22, 'O');
  setPx(g, 15, 20, 'D'); setPx(g, 16, 20, 'D');
  setPx(g, 15, 21, 'D'); setPx(g, 16, 21, 'D');
  setPx(g, 15, 22, 'D'); setPx(g, 16, 22, 'D');
  setPx(g, 11, 20, 'D'); setPx(g, 20, 20, 'D');
  setPx(g, 11, 21, 'D'); setPx(g, 20, 21, 'D');
  // Torso
  fillRect(g, 13, 23, 18, 26, 'M');
  setPx(g, 15, 23, 'H'); setPx(g, 16, 23, 'H');
  setPx(g, 15, 24, 'H'); setPx(g, 16, 24, 'H');
  setPx(g, 15, 25, 'H'); setPx(g, 16, 25, 'H');
  setPx(g, 12, 23, 'O'); setPx(g, 19, 23, 'O');
  setPx(g, 12, 24, 'O'); setPx(g, 19, 24, 'O');
  setPx(g, 12, 25, 'O'); setPx(g, 19, 25, 'O');
  setPx(g, 12, 26, 'O'); setPx(g, 19, 26, 'O');
  fillRect(g, 13, 26, 18, 26, 'D');
  // Legs (mid-stride)
  fillRect(g, 13, 27, 15, 30, 'M');
  fillRect(g, 16, 27, 18, 30, 'M');
  fillRect(g, 15, 27, 15, 30, 'D');
  fillRect(g, 18, 27, 18, 30, 'D');
  setPx(g, 12, 27, 'O'); setPx(g, 12, 28, 'O'); setPx(g, 12, 29, 'O'); setPx(g, 12, 30, 'O');
  setPx(g, 19, 27, 'O'); setPx(g, 19, 28, 'O'); setPx(g, 19, 29, 'O'); setPx(g, 19, 30, 'O');
  setPx(g, 15, 27, 'O'); setPx(g, 16, 27, 'O');
  setPx(g, 15, 28, 'O'); setPx(g, 15, 29, 'O'); setPx(g, 15, 30, 'O');
  setPx(g, 16, 28, 'O'); setPx(g, 16, 29, 'O'); setPx(g, 16, 30, 'O');
  fillRect(g, 12, 31, 15, 31, 'O');
  fillRect(g, 16, 31, 19, 31, 'O');
  setPx(g, 13, 30, 'D'); setPx(g, 14, 30, 'D');
  setPx(g, 17, 30, 'D'); setPx(g, 18, 30, 'D');
}

function drawWalkingBody(g: GridCell[][]): void {
  // Hat crown — slightly forward of body center
  fillRect(g, 12, 1, 18, 1, 'M');
  fillRect(g, 11, 2, 19, 2, 'M');
  fillRect(g, 10, 3, 20, 3, 'M');
  fillRect(g, 10, 4, 20, 4, 'M');
  fillRect(g, 10, 5, 20, 5, 'M');
  fillRect(g, 10, 6, 20, 6, 'M');
  fillRect(g, 13, 1, 17, 1, 'H');
  fillRect(g, 12, 2, 15, 2, 'H');
  fillRect(g, 11, 6, 19, 6, 'D');
  fillRect(g, 13, 0, 17, 0, 'O');
  setPx(g, 12, 1, 'O'); setPx(g, 18, 1, 'O');
  setPx(g, 11, 2, 'O'); setPx(g, 19, 2, 'O');
  setPx(g, 10, 3, 'O'); setPx(g, 20, 3, 'O');
  setPx(g, 10, 4, 'O'); setPx(g, 20, 4, 'O');
  setPx(g, 10, 5, 'O'); setPx(g, 20, 5, 'O');
  setPx(g, 10, 6, 'O'); setPx(g, 20, 6, 'O');
  // Near-side earflap
  fillRect(g, 8, 7, 9, 8, 'M');
  setPx(g, 8, 7, 'H'); setPx(g, 9, 7, 'H');
  setPx(g, 7, 7, 'O'); setPx(g, 7, 8, 'O');
  setPx(g, 8, 9, 'O'); setPx(g, 9, 9, 'O');
  // Brim — extends front and back
  fillRect(g, 6, 8, 22, 8, 'H');
  fillRect(g, 7, 9, 21, 9, 'D');
  setPx(g, 5, 8, 'O'); setPx(g, 23, 8, 'O');
  setPx(g, 6, 9, 'O'); setPx(g, 22, 9, 'O');
  fillRect(g, 6, 10, 22, 10, 'O');
  // Face (profile, facing right)
  fillRect(g, 12, 11, 19, 11, 'M');
  fillRect(g, 12, 12, 20, 12, 'M');
  fillRect(g, 12, 13, 20, 13, 'M');
  fillRect(g, 12, 14, 19, 14, 'M');
  fillRect(g, 12, 15, 19, 15, 'M');
  fillRect(g, 13, 16, 18, 16, 'M');
  setPx(g, 11, 11, 'O'); setPx(g, 20, 11, 'O');
  setPx(g, 11, 12, 'O'); setPx(g, 21, 12, 'O');
  setPx(g, 11, 13, 'O'); setPx(g, 21, 13, 'O');
  setPx(g, 11, 14, 'O'); setPx(g, 20, 14, 'O');
  setPx(g, 11, 15, 'O'); setPx(g, 20, 15, 'O');
  setPx(g, 12, 16, 'O'); setPx(g, 19, 16, 'O');
  fillRect(g, 13, 17, 18, 17, 'O');
  fillRect(g, 12, 11, 19, 11, 'D');
  // Single eye + mustache
  setPx(g, 15, 13, 'C');
  setPx(g, 16, 13, 'O');
  fillRect(g, 14, 15, 18, 15, 'C');
  // Neck
  fillRect(g, 14, 18, 16, 18, 'D');
  setPx(g, 13, 18, 'O'); setPx(g, 17, 18, 'O');
  // Cape — windswept trailing back, lifted (doesn't reach legs)
  fillRect(g, 12, 19, 17, 19, 'M');
  fillRect(g, 12, 19, 17, 19, 'H');
  setPx(g, 11, 19, 'O'); setPx(g, 18, 19, 'O');
  fillRect(g, 10, 20, 17, 20, 'M');
  fillRect(g, 9, 21, 16, 21, 'M');
  setPx(g, 12, 20, 'D'); setPx(g, 11, 21, 'D');
  setPx(g, 9, 20, 'O'); setPx(g, 18, 20, 'O');
  setPx(g, 8, 21, 'O'); setPx(g, 17, 21, 'O');
  fillRect(g, 9, 22, 16, 22, 'O');
  // Slim torso
  fillRect(g, 14, 23, 16, 26, 'M');
  setPx(g, 15, 23, 'H'); setPx(g, 15, 24, 'H'); setPx(g, 15, 25, 'H');
  setPx(g, 13, 23, 'O'); setPx(g, 17, 23, 'O');
  setPx(g, 13, 24, 'O'); setPx(g, 17, 24, 'O');
  setPx(g, 13, 25, 'O'); setPx(g, 17, 25, 'O');
  setPx(g, 13, 26, 'O'); setPx(g, 17, 26, 'O');
  fillRect(g, 14, 26, 16, 26, 'D');
  // Legs — clean stride (front bent, back planted)
  setPx(g, 16, 27, 'M'); setPx(g, 17, 27, 'M');
  setPx(g, 17, 28, 'M'); setPx(g, 18, 28, 'M');
  setPx(g, 18, 29, 'M');
  setPx(g, 15, 27, 'O'); setPx(g, 18, 27, 'O');
  setPx(g, 16, 28, 'O'); setPx(g, 19, 28, 'O');
  setPx(g, 17, 29, 'O'); setPx(g, 19, 29, 'O');
  fillRect(g, 17, 30, 20, 30, 'O');
  setPx(g, 18, 30, 'D');
  fillRect(g, 13, 27, 14, 30, 'M');
  setPx(g, 14, 27, 'D'); setPx(g, 14, 28, 'D'); setPx(g, 14, 29, 'D'); setPx(g, 14, 30, 'D');
  setPx(g, 12, 27, 'O'); setPx(g, 12, 28, 'O'); setPx(g, 12, 29, 'O'); setPx(g, 12, 30, 'O');
  setPx(g, 15, 28, 'O'); setPx(g, 15, 29, 'O'); setPx(g, 15, 30, 'O');
  fillRect(g, 11, 31, 15, 31, 'O');
}

function drawArm(g: GridCell[][], pose: 'idle' | 'walking', raised: boolean): void {
  if (pose === 'idle' && !raised) {
    // Forward arm at chest level, hand at top-left of lens (22, 22)
    setPx(g, 19, 22, 'M'); setPx(g, 20, 22, 'M'); setPx(g, 21, 22, 'M');
    setPx(g, 19, 23, 'M'); setPx(g, 20, 23, 'M'); setPx(g, 21, 23, 'M');
    setPx(g, 19, 21, 'O'); setPx(g, 20, 21, 'O'); setPx(g, 21, 21, 'O');
    setPx(g, 22, 22, 'O');
    setPx(g, 19, 24, 'O'); setPx(g, 20, 24, 'O'); setPx(g, 21, 24, 'O');
    setPx(g, 19, 23, 'D'); setPx(g, 20, 23, 'D');
  } else if (pose === 'idle' && raised) {
    // "Looking through magnifier" — arm bent forward then up to face level
    setPx(g, 19, 22, 'M'); setPx(g, 20, 22, 'M');
    setPx(g, 19, 21, 'O'); setPx(g, 20, 21, 'O');
    setPx(g, 20, 20, 'M'); setPx(g, 21, 20, 'M');
    setPx(g, 21, 19, 'M'); setPx(g, 22, 19, 'M');
    setPx(g, 22, 18, 'M'); setPx(g, 23, 18, 'M');
    setPx(g, 22, 17, 'M'); setPx(g, 23, 17, 'M');
    setPx(g, 22, 16, 'M'); setPx(g, 23, 16, 'M');
    setPx(g, 22, 15, 'M'); setPx(g, 23, 15, 'M');
    setPx(g, 22, 16, 'H'); setPx(g, 22, 17, 'H');
    setPx(g, 21, 22, 'O'); setPx(g, 22, 20, 'O'); setPx(g, 23, 19, 'O');
    setPx(g, 24, 18, 'O'); setPx(g, 24, 17, 'O'); setPx(g, 24, 16, 'O'); setPx(g, 24, 15, 'O');
    setPx(g, 21, 19, 'O'); setPx(g, 21, 18, 'O'); setPx(g, 21, 17, 'O'); setPx(g, 21, 16, 'O'); setPx(g, 21, 15, 'O');
    setPx(g, 22, 14, 'O'); setPx(g, 23, 14, 'O');
  } else if (pose === 'walking' && !raised) {
    // Forward arm grips lens top-left at (18, 22)
    setPx(g, 16, 22, 'M'); setPx(g, 17, 22, 'M');
    setPx(g, 16, 23, 'M'); setPx(g, 17, 23, 'M');
    setPx(g, 17, 24, 'M');
    setPx(g, 15, 22, 'O'); setPx(g, 16, 21, 'O'); setPx(g, 17, 21, 'O');
    setPx(g, 18, 22, 'O'); setPx(g, 18, 23, 'O');
    setPx(g, 18, 24, 'O'); setPx(g, 16, 24, 'O'); setPx(g, 15, 23, 'O');
  } else {
    // Walking + raised — 2-col forearm forward then up
    setPx(g, 17, 23, 'M'); setPx(g, 18, 23, 'M');
    setPx(g, 17, 22, 'O'); setPx(g, 18, 22, 'O');
    setPx(g, 18, 21, 'M'); setPx(g, 19, 21, 'M');
    setPx(g, 19, 20, 'M'); setPx(g, 20, 20, 'M');
    setPx(g, 20, 19, 'M'); setPx(g, 21, 19, 'M');
    setPx(g, 20, 18, 'M'); setPx(g, 21, 18, 'M');
    setPx(g, 20, 17, 'M'); setPx(g, 21, 17, 'M');
    setPx(g, 20, 16, 'M'); setPx(g, 21, 16, 'M');
    setPx(g, 20, 15, 'M'); setPx(g, 21, 15, 'M');
    setPx(g, 20, 18, 'H'); setPx(g, 20, 17, 'H');
    setPx(g, 19, 23, 'O'); setPx(g, 20, 21, 'O');
    setPx(g, 22, 19, 'O'); setPx(g, 22, 18, 'O'); setPx(g, 22, 17, 'O'); setPx(g, 22, 16, 'O'); setPx(g, 22, 15, 'O');
    setPx(g, 19, 19, 'O'); setPx(g, 19, 18, 'O'); setPx(g, 19, 17, 'O'); setPx(g, 19, 16, 'O'); setPx(g, 19, 15, 'O');
    setPx(g, 20, 14, 'O'); setPx(g, 21, 14, 'O');
  }
}

function drawMagnifier(g: GridCell[][], pose: 'idle' | 'walking', raised: boolean): void {
  // 6×6 round lens (all 4 corners chipped) + short diagonal handle
  let lx: number, ly: number;
  if (pose === 'idle' && !raised) { lx = 22; ly = 22; }
  else if (pose === 'idle' && raised) { lx = 17; ly = 10; }
  else if (pose === 'walking' && !raised) { lx = 18; ly = 22; }
  else { lx = 15; ly = 10; }

  setPx(g, lx + 1, ly, 'O'); setPx(g, lx + 2, ly, 'O'); setPx(g, lx + 3, ly, 'O'); setPx(g, lx + 4, ly, 'O');
  setPx(g, lx, ly + 1, 'O'); setPx(g, lx + 5, ly + 1, 'O');
  setPx(g, lx, ly + 2, 'O'); setPx(g, lx + 5, ly + 2, 'O');
  setPx(g, lx, ly + 3, 'O'); setPx(g, lx + 5, ly + 3, 'O');
  setPx(g, lx, ly + 4, 'O'); setPx(g, lx + 5, ly + 4, 'O');
  setPx(g, lx + 1, ly + 5, 'O'); setPx(g, lx + 2, ly + 5, 'O'); setPx(g, lx + 3, ly + 5, 'O'); setPx(g, lx + 4, ly + 5, 'O');
  fillRect(g, lx + 1, ly + 1, lx + 4, ly + 4, 'H');
  setPx(g, lx + 1, ly + 1, 'C');
  setPx(g, lx + 5, ly + 5, 'O');
  setPx(g, lx + 6, ly + 6, 'O');
}

function drawPipe(g: GridCell[][], pose: 'idle' | 'walking'): void {
  if (pose === 'idle') {
    fillRect(g, 23, 15, 24, 15, 'D');
    setPx(g, 22, 15, 'O');
    setPx(g, 23, 16, 'O'); setPx(g, 24, 16, 'O');
    setPx(g, 23, 14, 'O'); setPx(g, 24, 14, 'O');
    fillRect(g, 25, 13, 26, 14, 'M');
    setPx(g, 25, 13, 'H');
    setPx(g, 25, 12, 'O'); setPx(g, 26, 12, 'O');
    setPx(g, 27, 13, 'O'); setPx(g, 27, 14, 'O');
    setPx(g, 25, 15, 'O'); setPx(g, 26, 15, 'O');
  } else {
    fillRect(g, 21, 14, 22, 14, 'D');
    setPx(g, 21, 15, 'O'); setPx(g, 22, 15, 'O');
    setPx(g, 21, 13, 'O'); setPx(g, 22, 13, 'O');
    fillRect(g, 23, 12, 24, 13, 'M');
    setPx(g, 23, 12, 'H');
    setPx(g, 23, 11, 'O'); setPx(g, 24, 11, 'O');
    setPx(g, 25, 12, 'O'); setPx(g, 25, 13, 'O');
    setPx(g, 23, 14, 'O'); setPx(g, 24, 14, 'O');
  }
}

interface BuildOpts {
  pose: 'idle' | 'walking';
  smoking: boolean;
  raised: boolean;
}

function buildSprite(opts: BuildOpts): SpriteLayers {
  const g = makeGrid();
  if (opts.pose === 'idle') drawIdleBody(g);
  else drawWalkingBody(g);
  drawArm(g, opts.pose, opts.raised);
  drawMagnifier(g, opts.pose, opts.raised);
  if (opts.smoking) drawPipe(g, opts.pose);
  return gridToLayers(g);
}

// Pre-bake the 4 runtime variants
//   • idleDefault         (no smoke, low magnifier)
//   • idleCelebration     (pipe + raised magnifier — when burst fires)
//   • walkingDefault      (pipe always + low magnifier)
//   • walkingCelebration  (pipe + raised magnifier — when burst fires while walking)
const SPRITE_VARIANTS = {
  idleDefault: buildSprite({ pose: 'idle', smoking: false, raised: false }),
  idleCelebration: buildSprite({ pose: 'idle', smoking: true, raised: true }),
  walkingDefault: buildSprite({ pose: 'walking', smoking: true, raised: false }),
  walkingCelebration: buildSprite({ pose: 'walking', smoking: true, raised: true }),
} as const;

// Idle smoke origin (above pipe bowl) — used for animated puff rects
const IDLE_PUFF_X = 26;
const IDLE_PUFF_Y = 11;
const WALKING_PUFF_X = 24;
const WALKING_PUFF_Y = 10;

// Completion burst — 16 puffs scattered above the pipe
const BURST_PUFFS: ReadonlyArray<SpritePixel> = [
  [26, 11], [27, 11], [25, 11], [28, 11],
  [26, 10], [27, 10], [25, 10], [28, 10],
  [26, 9],  [27, 9],  [25, 9],  [29, 10],
  [26, 8],  [27, 8],  [28, 8],  [29, 8],
];

interface DetectiveSpriteProps {
  walking?: boolean;
  smokeBurst?: boolean;
  /** kept for backwards-compat; v3 sprite handles eye accents inline */
  winking?: boolean;
}

function PixelRect({ x, y }: { x: number; y: number }): ReactElement {
  return <rect x={x * PIXEL} y={y * PIXEL} width={PIXEL} height={PIXEL} />;
}

function LayerGroup({
  pixels,
  fill,
  keyPrefix,
}: {
  pixels: ReadonlyArray<SpritePixel>;
  fill: string;
  keyPrefix: string;
}): ReactElement {
  return (
    <g fill={fill}>
      {pixels.map(([x, y]) => (
        <PixelRect key={`${keyPrefix}-${x}-${y}`} x={x} y={y} />
      ))}
    </g>
  );
}

function DetectiveSprite({
  walking = true,
  smokeBurst = false,
}: DetectiveSpriteProps): ReactElement {
  const variant = smokeBurst
    ? walking
      ? SPRITE_VARIANTS.walkingCelebration
      : SPRITE_VARIANTS.idleCelebration
    : walking
      ? SPRITE_VARIANTS.walkingDefault
      : SPRITE_VARIANTS.idleDefault;

  // Smoke puffs only render when the buddy is in a "smoking" state
  //   • walking: pipe always visible → puffs cycle
  //   • idle + burst: pipe visible during celebration
  //   • idle without burst: no smoke
  const showSmoke = walking || smokeBurst;
  const puffX = walking ? WALKING_PUFF_X : IDLE_PUFF_X;
  const puffY = walking ? WALKING_PUFF_Y : IDLE_PUFF_Y;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${SPRITE_COLS * PIXEL} ${SPRITE_ROWS * PIXEL}`}
      width={CHAR_WIDTH}
      height={CHAR_HEIGHT}
      sx={{
        color: 'primary.main',
        display: 'block',
        overflow: 'visible',
        imageRendering: 'pixelated',
        '& .body-group': {
          animation: walking
            ? 'detective-bob 0.55s steps(1, end) infinite'
            : 'detective-breathe 4s ease-in-out infinite',
        },
        '& .puff-1': { animation: `detective-puff ${IDLE_SMOKE_CYCLE_SECONDS}s ease-out 0s infinite` },
        '& .puff-2': { animation: `detective-puff ${IDLE_SMOKE_CYCLE_SECONDS}s ease-out 0.8s infinite` },
        '& .puff-3': { animation: `detective-puff ${IDLE_SMOKE_CYCLE_SECONDS}s ease-out 1.6s infinite` },
        '& .puff-4': { animation: `detective-puff ${IDLE_SMOKE_CYCLE_SECONDS}s ease-out 2.4s infinite` },
        '& .puff-5': { animation: `detective-puff ${IDLE_SMOKE_CYCLE_SECONDS}s ease-out 3.2s infinite` },
        '& .puff-burst': { opacity: smokeBurst ? 1 : 0 },
        '& .puff-burst-1':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out 0s infinite'    : 'none' },
        '& .puff-burst-2':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.08s infinite' : 'none' },
        '& .puff-burst-3':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.15s infinite' : 'none' },
        '& .puff-burst-4':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.23s infinite' : 'none' },
        '& .puff-burst-5':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.30s infinite' : 'none' },
        '& .puff-burst-6':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.38s infinite' : 'none' },
        '& .puff-burst-7':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.45s infinite' : 'none' },
        '& .puff-burst-8':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.53s infinite' : 'none' },
        '& .puff-burst-9':  { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.60s infinite' : 'none' },
        '& .puff-burst-10': { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.68s infinite' : 'none' },
        '& .puff-burst-11': { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.75s infinite' : 'none' },
        '& .puff-burst-12': { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.83s infinite' : 'none' },
        '& .puff-burst-13': { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.90s infinite' : 'none' },
        '& .puff-burst-14': { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -0.98s infinite' : 'none' },
        '& .puff-burst-15': { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -1.05s infinite' : 'none' },
        '& .puff-burst-16': { animation: smokeBurst ? 'detective-puff-burst 1.2s ease-out -1.13s infinite' : 'none' },
        '@keyframes detective-bob': {
          '0%, 49.99%': { transform: 'translateY(0)' },
          '50%, 100%': { transform: 'translateY(-1px)' },
        },
        '@keyframes detective-breathe': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-1px)' },
        },
        '@keyframes detective-puff': {
          '0%': { opacity: 0, transform: 'translate(0, 0)' },
          '0.67%': { opacity: 0.75, transform: 'translate(0, -4px)' },
          '2.67%': { opacity: 0.55, transform: 'translate(4px, -14px)' },
          '4.44%': { opacity: 0, transform: 'translate(10px, -26px)' },
          '100%': { opacity: 0, transform: 'translate(10px, -26px)' },
        },
        '@keyframes detective-puff-burst': {
          '0%': { opacity: 0, transform: 'translate(0, 0)' },
          '10%': { opacity: 0.9, transform: 'translate(2px, -3px)' },
          '40%': { opacity: 0.8, transform: 'translate(8px, -12px)' },
          '70%': { opacity: 0.5, transform: 'translate(18px, -24px)' },
          '100%': { opacity: 0, transform: 'translate(26px, -40px)' },
        },
      }}
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      <g className="body-group">
        <LayerGroup pixels={variant.outline}   fill={OUTLINE_FILL}    keyPrefix="ol" />
        <LayerGroup pixels={variant.shadow}    fill={SHADOW_FILL}     keyPrefix="sh" />
        <LayerGroup pixels={variant.mid}       fill="currentColor"    keyPrefix="md" />
        <LayerGroup pixels={variant.highlight} fill={HIGHLIGHT_AMBER} keyPrefix="hl" />
        <LayerGroup pixels={variant.cream}     fill={HIGHLIGHT_FILL}  keyPrefix="cr" />
      </g>

      {/* Idle smoke wisps — only render when the buddy is in a smoking state */}
      {showSmoke &&
        ([1, 2, 3, 4, 5] as const).map((n) => (
          <rect
            key={`puff-${n}`}
            className={`puff-${n}`}
            x={puffX * PIXEL}
            y={puffY * PIXEL}
            width={PUFF_SIZE}
            height={PUFF_SIZE}
            fill={HIGHLIGHT_FILL}
          />
        ))}

      {/* Completion burst — 16 puffs */}
      {BURST_PUFFS.map(([x, y], i) => (
        <rect
          key={`burst-${i}`}
          className={`puff-burst puff-burst-${i + 1}`}
          x={x * PIXEL}
          y={y * PIXEL}
          width={BURST_PUFF_SIZE}
          height={BURST_PUFF_SIZE}
          fill={HIGHLIGHT_FILL}
        />
      ))}
    </Box>
  );
}

// ─── MegaSmokeCurtain (#4) ────────────────────────────────────────────────────
//
// Fixed overlay covering the bottom half of the screen. 40 puffs distributed
// across the bottom with randomized X/Y and per-puff animation delay.
// Fade in 300ms → hold ~2.5s → fade out 700ms (~3500ms total).

const MEGA_PUFFS = 40;
const MEGA_PUFF_SIZE = BURST_PUFF_SIZE * 2;

// Deterministic pseudo-random positions so the curtain is stable across renders.
const MEGA_PUFF_DATA: ReadonlyArray<{ xPct: number; yPct: number; delay: number }> =
  Array.from({ length: MEGA_PUFFS }, (_, i) => ({
    xPct: (i * 7919 + 31) % 97,
    yPct: (i * 6271 + 17) % 75,
    delay: Math.round((i * 400) / MEGA_PUFFS),
  }));

function MegaSmokeCurtain({ active }: { active: boolean }): ReactElement | null {
  if (!active) return null;
  return (
    <Box
      aria-hidden="true"
      data-testid="mega-smoke-curtain"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: '50vh',
        pointerEvents: 'none',
        zIndex: 6,
        animation: 'mega-smoke-lifecycle 3.5s ease forwards',
        '@keyframes mega-smoke-lifecycle': {
          '0%': { opacity: 0 },
          '8.6%': { opacity: 1 },
          '71.4%': { opacity: 1 },
          '100%': { opacity: 0 },
        },
      }}
    >
      {MEGA_PUFF_DATA.map(({ xPct, yPct, delay }, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            left: `${xPct}%`,
            bottom: `${yPct}%`,
            width: MEGA_PUFF_SIZE,
            height: MEGA_PUFF_SIZE,
            backgroundColor: 'rgba(255, 245, 230, 0.55)',
            borderRadius: '50%',
            filter: 'blur(4px)',
            animation: `mega-puff-rise 1.4s ease-out ${delay}ms infinite`,
            '@keyframes mega-puff-rise': {
              '0%': { opacity: 0, transform: 'translate(0, 0) scale(0.6)' },
              '20%': { opacity: 0.9 },
              '70%': { opacity: 0.5, transform: `translate(${(i % 5) - 2}px, -24px) scale(1.3)` },
              '100%': { opacity: 0, transform: `translate(${(i % 7) - 3}px, -48px) scale(1.8)` },
            },
          }}
        />
      ))}
    </Box>
  );
}

// ─── Bubble tail ──────────────────────────────────────────────────────────────

function BubbleTail(): ReactElement {
  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 8,
          height: 3,
          backgroundColor: 'primary.strong',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 3,
          left: 1.5,
          width: 5,
          height: 3,
          backgroundColor: 'primary.strong',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 6,
          left: 3,
          width: 2,
          height: 3,
          backgroundColor: 'primary.strong',
        }}
      />
    </>
  );
}

// ─── Walking track ────────────────────────────────────────────────────────────

function WalkingTrack({
  hovered,
  bubbleNode,
  smokeBurst,
  showBubble,
  winking,
}: {
  hovered: boolean;
  bubbleNode: ReactNode;
  smokeBurst: boolean;
  showBubble?: boolean;
  winking: boolean;
}): ReactElement {
  return (
    <Box
      aria-hidden="true"
      sx={{
        flex: 1,
        position: 'relative',
        height: '100%',
        overflow: 'visible',
        mx: 1,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          bottom: 6,
          width: '100%',
          height: CHAR_HEIGHT,
          pointerEvents: 'none',
          animation: 'detective-walk-translate 40s linear infinite',
          animationPlayState: hovered ? 'paused' : 'running',
          '@keyframes detective-walk-translate': {
            '0%': { transform: `translateX(0)` },
            '50%': { transform: `translateX(calc(100% - ${CHAR_WIDTH}px))` },
            '100%': { transform: `translateX(0)` },
          },
        }}
      >
        {showBubble !== false && (
          <Box
            sx={{
              position: 'absolute',
              left: CHAR_WIDTH / 2,
              bottom: CHAR_HEIGHT + 4,
              pointerEvents: 'auto',
              maxWidth: 240,
              animation: 'bubble-anchor 40s linear infinite',
              animationPlayState: hovered ? 'paused' : 'running',
              '@keyframes bubble-anchor': {
                '0%': { transform: 'translateX(0%)' },
                '50%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(0%)' },
              },
              '& > .bubble-body': {
                display: 'inline-block',
                maxWidth: 280,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                backgroundColor: 'background.deeper',
                border: '1px solid',
                borderColor: 'primary.strong',
                borderRadius: 1.5,
                padding: '6px 12px',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 12,
                color: 'primary.main',
                lineHeight: 1.4,
                boxShadow:
                  '0 4px 14px rgba(0, 0, 0, 0.55), inset 0 0 12px rgba(217, 119, 87, 0.08)',
                position: 'relative',
              },
            }}
          >
            <Box className="bubble-body">
              {bubbleNode}
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  bottom: -9,
                  width: 8,
                  height: 9,
                  pointerEvents: 'none',
                  left: 'calc(0% - 4px)',
                  animation: 'detective-walking-tail 40s linear infinite',
                  animationPlayState: hovered ? 'paused' : 'running',
                  '@keyframes detective-walking-tail': {
                    '0%': { left: 'calc(0% - 4px)' },
                    '50%': { left: 'calc(100% - 4px)' },
                    '100%': { left: 'calc(0% - 4px)' },
                  },
                }}
              >
                <BubbleTail />
              </Box>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'inline-block',
            position: 'relative',
            zIndex: 2,
            animation: 'detective-walk-flip 40s linear infinite',
            animationPlayState: hovered ? 'paused' : 'running',
            '@keyframes detective-walk-flip': {
              '0%, 49.99%': { transform: 'scaleX(1)' },
              '50%, 99.99%': { transform: 'scaleX(-1)' },
              '100%': { transform: 'scaleX(1)' },
            },
          }}
        >
          <DetectiveSprite walking={true} smokeBurst={smokeBurst} winking={winking} />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Stationary buddy (idle state) ────────────────────────────────────────────

function StationaryBuddy({
  bubbleNode,
  smokeBurst,
  showBubble,
  winking,
}: {
  bubbleNode: ReactNode;
  smokeBurst: boolean;
  showBubble?: boolean;
  winking: boolean;
}): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0,
        pointerEvents: 'auto',
      }}
    >
      {showBubble !== false && (
        <Box
          sx={{
            position: 'relative',
            maxWidth: 280,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            backgroundColor: 'background.deeper',
            border: '1px solid',
            borderColor: 'primary.strong',
            borderRadius: 1.5,
            padding: '6px 12px',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            color: 'primary.main',
            lineHeight: 1.4,
            boxShadow:
              '0 4px 14px rgba(0, 0, 0, 0.55), inset 0 0 12px rgba(217, 119, 87, 0.08)',
            mb: '7px',
          }}
        >
          {bubbleNode}
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              bottom: -9,
              right: CHAR_WIDTH / 2 - 2.5,
              width: 8,
              height: 9,
              pointerEvents: 'none',
            }}
          >
            <BubbleTail />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          width: CHAR_WIDTH,
          height: CHAR_HEIGHT,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <DetectiveSprite walking={false} smokeBurst={smokeBurst} winking={winking} />
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function DetectiveFooter({
  label = '🕵🏽 investigating…',
  staticLabel,
  action,
}: DetectiveFooterProps): ReactElement {
  const { collapsed, mode } = useSidebarState();
  const reduced = useReducedMotion();
  const [hovered, setHovered] = useState(false);

  type TrackedJob = { type: 'investigation' | 'analysis' | 'intake'; id: string };
  const [activeJobs, setActiveJobs] = useState<Map<string, TrackedJob>>(
    () => new Map(),
  );
  const activeJobsRef = useRef<Map<string, TrackedJob>>(activeJobs);
  activeJobsRef.current = activeJobs;

  // Completion smoke burst — set to true for ~10s after a done/failed event (#3).
  const [smokeBurst, setSmokeBurst] = useState(false);
  const burstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // MegaSmokeCurtain state — triggered by double-click (#4).
  const [megaSmoke, setMegaSmoke] = useState(false);
  const megaSmokeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Wink state — every ~12s, for 180ms (#5).
  const [winking, setWinking] = useState(false);
  const winkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [bubbleVisible, setBubbleVisible] = useState(true);
  useEffect(() => {
    function onVisibility(e: Event): void {
      const detail = (e as CustomEvent<{ visible: boolean }>).detail;
      setBubbleVisible(detail.visible);
    }
    window.addEventListener('sherlock:buddy-bubble-visibility', onVisibility as EventListener);
    return () =>
      window.removeEventListener('sherlock:buddy-bubble-visibility', onVisibility as EventListener);
  }, []);

  // Wink cycle
  useEffect(() => {
    if (reduced) return;
    let active = true;
    const scheduleWink = (): void => {
      winkTimerRef.current = setTimeout(() => {
        if (!active) return;
        setWinking(true);
        setTimeout(() => {
          if (!active) return;
          setWinking(false);
          scheduleWink();
        }, 180);
      }, 12_000);
    };
    scheduleWink();
    return () => {
      active = false;
      if (winkTimerRef.current) clearTimeout(winkTimerRef.current);
    };
  }, [reduced]);

  function fireSmokeBurst(): void {
    if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
    setSmokeBurst(true);
    // #3: extended from 4000ms to 10000ms
    burstTimerRef.current = setTimeout(() => setSmokeBurst(false), 10_000);
  }

  useEffect(() => {
    function onContext(e: Event): void {
      const detail = (
        e as CustomEvent<
          | {
              kind: 'job';
              status: 'running' | 'done' | 'failed';
              identifier?: string;
              job?: { type: 'investigation' | 'analysis'; id: string };
            }
          | {
              kind: 'intake';
              status: 'running' | 'paused' | 'done' | 'failed';
              phase: string;
              coverage?: { covered: number; total: number };
              sessionId?: string;
            }
        >
      ).detail;

      if (detail.kind === 'intake') {
        const sessionId = detail.sessionId ?? '__intake__';
        const key = `intake:${sessionId}`;
        const wasTracking = activeJobsRef.current.has(key);
        setActiveJobs((prev) => {
          const next = new Map(prev);
          if (detail.status === 'running') {
            next.set(key, { type: 'intake', id: sessionId });
          } else {
            // paused, done, or failed — stop walking
            next.delete(key);
          }
          return next;
        });
        // celebrate on done or failed; NOT on paused (round ready, user's turn)
        if ((detail.status === 'done' || detail.status === 'failed') && wasTracking) {
          fireSmokeBurst();
        }
        return;
      }

      // kind === 'job' (investigation / analysis)
      const fallback = detail.identifier ?? '__unidentified__';
      const key = detail.job ? `${detail.job.type}:${detail.job.id}` : fallback;
      const wasTracking = activeJobsRef.current.has(key);
      setActiveJobs((prev) => {
        const next = new Map(prev);
        if (detail.status === 'running') {
          if (detail.job) {
            next.set(key, detail.job);
          } else {
            next.set(key, { type: 'investigation', id: '' });
          }
        } else {
          next.delete(key);
        }
        return next;
      });
      if ((detail.status === 'done' || detail.status === 'failed') && wasTracking) {
        fireSmokeBurst();
      }
    }
    window.addEventListener('sherlock:buddy-context', onContext as EventListener);
    return () => {
      window.removeEventListener('sherlock:buddy-context', onContext as EventListener);
      if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
    };
  }, []);

  // Polling reconciler — closes the "navigated away mid-job" loop.
  useEffect(() => {
    const RECONCILE_INTERVAL_MS = 8_000;
    const reconcileable = Array.from(activeJobs.entries()).filter(
      ([, job]) => job.id !== '',
    );
    if (reconcileable.length === 0) return;

    let cancelled = false;
    const handle = setInterval(() => {
      if (cancelled) return;
      void reconcileable.forEach(async ([key, job]) => {
        try {
          let terminal = false;
          if (job.type === 'investigation') {
            const data = await investigate.get(job.id);
            terminal = data.status === 'done' || data.status === 'failed';
          } else if (job.type === 'intake') {
            const data = await intake.get(job.id);
            terminal =
              data.status === 'preview' ||
              data.status === 'confirmed' ||
              data.status === 'abandoned';
          } else {
            const data = await analyses.get(job.id);
            terminal = isAnalysisResult(data) || isAnalysisFailed(data);
          }
          if (!terminal || cancelled) return;
          if (!activeJobsRef.current.has(key)) return;
          setActiveJobs((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Map(prev);
            next.delete(key);
            return next;
          });
          fireSmokeBurst();
        } catch {
          // Swallow — single poll failure doesn't break the reconciler.
        }
      });
    }, RECONCILE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [activeJobs]);

  const isActive = activeJobs.size > 0;

  let leftOffset: number | string;
  if (mode === 'drawer') {
    leftOffset = 0;
  } else if (collapsed) {
    leftOffset = SIDEBAR_COLLAPSED;
  } else {
    leftOffset = SIDEBAR_EXPANDED;
  }

  function handleDetectiveClick(): void {
    window.dispatchEvent(new CustomEvent('sherlock:buddy-clicked'));
    // #4: Double-click triggers MegaSmokeCurtain
    if (megaSmokeTimerRef.current) clearTimeout(megaSmokeTimerRef.current);
    setMegaSmoke(true);
    megaSmokeTimerRef.current = setTimeout(() => setMegaSmoke(false), 3_500);
  }

  // ── Reduced-motion: static fallback ─────────────────────────────────────
  if (reduced) {
    return (
      <Box
        data-testid="detective-footer"
        data-reduced-motion="true"
        role="status"
        aria-label={label}
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 16,
          zIndex: 5,
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
        }}
      >
        <BuddyBubble override={staticLabel} />
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Box>
    );
  }

  // ── IDLE state ────────────────────────────────────────────────────────────
  if (!isActive) {
    return (
      <>
        <MegaSmokeCurtain active={megaSmoke} />
        <Box
          data-testid="detective-footer"
          role="status"
          aria-label={label}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 16,
            zIndex: 5,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 1,
          }}
        >
          <Box
            sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
            onDoubleClick={handleDetectiveClick}
            aria-label="Double-click the detective to get a status update"
            role="button"
            tabIndex={0}
          >
            <StationaryBuddy
              bubbleNode={<BuddyBubble key="buddy-singleton" override={staticLabel} />}
              smokeBurst={smokeBurst}
              showBubble={bubbleVisible}
              winking={winking}
            />
          </Box>
          {action && <Box sx={{ pointerEvents: 'auto' }}>{action}</Box>}
        </Box>
      </>
    );
  }

  // ── ACTIVE state: walking the 25% track at the right edge ─────────────────
  return (
    <>
      <MegaSmokeCurtain active={megaSmoke} />
      <Box
        data-testid="detective-footer"
        role="status"
        aria-label={label}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: leftOffset,
          right: 16,
          height: { xs: 76, md: 84 },
          bgcolor: 'transparent',
          zIndex: 5,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'stretch',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            width: '25%',
            minWidth: 200,
            height: '100%',
            position: 'relative',
            pointerEvents: 'auto',
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onDoubleClick={handleDetectiveClick}
          style={{ cursor: 'pointer' }}
          aria-label="Double-click the detective to get a status update"
          role="button"
          tabIndex={0}
        >
          <WalkingTrack
            hovered={hovered}
            bubbleNode={<BuddyBubble key="buddy-singleton" override={staticLabel} />}
            smokeBurst={smokeBurst}
            showBubble={bubbleVisible}
            winking={winking}
          />
        </Box>

        {action && (
          <Box
            sx={{
              position: 'absolute',
              right: 16,
              bottom: 12,
              pointerEvents: 'auto',
            }}
          >
            {action}
          </Box>
        )}
      </Box>
    </>
  );
}
